import { AppleIcon, GooglePlayIcon } from "./StoreIcons";
import qrDownload from "../assets/qr/qr-download.svg";

/**
 * Big two-line store buttons and a single download QR code, for patient landing pages.
 * They go through our own /appstore and /playstore redirects (vercel.json), so the store URLs live in one place.
 */
export const STORE_LINKS = { apple: "/appstore", google: "/playstore" } as const;

export type Store = keyof typeof STORE_LINKS;

const focusRing =
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500";

export function StoreButton({ store, className = "" }: { store: Store; className?: string }) {
    const apple = store === "apple";
    return (
        <a
            href={STORE_LINKS[store]}
            className={`group flex h-14 min-w-0 items-center justify-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-4 text-slate-900 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-slate-50 hover:shadow-md motion-reduce:transition-none motion-reduce:hover:translate-y-0 sm:px-5 dark:border-zinc-700 dark:bg-black dark:text-white dark:hover:bg-zinc-900 ${focusRing} ${className}`}
        >
            {apple ? <AppleIcon height={24} /> : <GooglePlayIcon height={22} />}
            <span className="flex flex-col items-start leading-none">
                <span className="text-[11px] font-normal text-slate-500 dark:text-zinc-400">{apple ? "Download on the" : "Get it on"}</span>
                <span className="mt-1 whitespace-nowrap text-base font-semibold">{apple ? "App Store" : "Google Play"}</span>
            </span>
        </a>
    );
}

/** One QR code for every phone: it opens quilt.gg/download, which sends iPhones to the App Store and Android to Google Play. */
export function DownloadQr({ caption = "Scan with your phone", size = "md" }: { caption?: string; size?: "md" | "lg" }) {
    const box = size === "lg" ? "h-36 w-36" : "h-28 w-28";
    return (
        <figure className="flex flex-col items-center gap-2">
            <img
                src={qrDownload}
                alt="QR code that opens Quilt in your phone's app store"
                width={size === "lg" ? 144 : 112}
                height={size === "lg" ? 144 : 112}
                loading="lazy"
                className={`${box} rounded-xl bg-white p-2 shadow-sm ring-1 ring-slate-200 dark:ring-zinc-700`}
            />
            {caption && <figcaption className="text-xs font-medium text-slate-500 dark:text-zinc-400">{caption}</figcaption>}
        </figure>
    );
}
